
/**
 * A simple and robust Vue directive for formatting number inputs in real-time,
 * designed for Persian (Farsi) and RTL interfaces.
 *
 * Features:
 * - Converts Persian/Arabic digits to English for processing using a reliable char-code method.
 * - Formats numbers with thousands separators.
 * - Supports 'rial' (integer only) and 'gold' (optional decimals) types.
 * - For 'gold', decimals are only activated when the user types a decimal point ('.' or '٫').
 * - Handles unlimited integer length using BigInt.
 * - On blur, converts the formatted number back to Persian digits for display.
 * - Prioritizes stability in RTL by keeping the cursor at the end during input.
 */

// --- Self-contained and robust digit conversion utilities ---
const PERSIAN_ZERO_CODE = '۰'.charCodeAt(0);
const ARABIC_ZERO_CODE = '٠'.charCodeAt(0);

function toEnglishDigits(str) {
  if (str === null || str === undefined) return '';
  // Use charCode arithmetic for robust digit conversion.
  return String(str)
    .replace(/[۰-��]/g, (d) => d.charCodeAt(0) - PERSIAN_ZERO_CODE)
    .replace(/[٠-٩]/g, (d) => d.charCodeAt(0) - ARABIC_ZERO_CODE);
}

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
function toPersianDigits(str) {
  if (str === null || str === undefined) return '';
  return String(str).replace(/[0-9]/g, (d) => PERSIAN_DIGITS[parseInt(d)]);
}
// --- End of utilities ---

function setupInputFormatting(el, binding) {
  const getOptions = () => {
    const decimalsAttr = el.getAttribute('data-decimals');
    const typeAttr = el.getAttribute('data-type');
    let decimals = decimalsAttr ? parseInt(decimalsAttr, 10) : null;
    // Default to 3 decimals for gold, 0 for rial if not specified.
    if (isNaN(decimals) || decimals < 0) {
      decimals = typeAttr === 'gold' ? 3 : 0;
    }
    return {
      decimals,
      type: typeAttr || 'gold',
    };
  };

  const dispatchEvent = (value) => {
    el.value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const syncFormattedDisplay = ({ usePersianDigits = false } = {}) => {
    if (el.value === null || el.value === undefined || el.value === '') {
      return;
    }

    const options = getOptions();
    const { formatted } = formatValue(el.value, options);
    const nextValue = usePersianDigits ? toPersianDigits(formatted) : formatted;

    if (el.value !== nextValue) {
      el.value = nextValue;
    }
  };

  // Utility to move cursor to the end. Best for RTL numeric inputs.
  const setCaretToEnd = () => {
    try {
      const len = el.value.length;
      el.setSelectionRange(len, len);
    } catch (e) {
      // Fails on some input types or browsers, but safe to ignore.
    }
  };

  const formatValue = (rawStr, options) => {
    if (rawStr === null || rawStr === undefined || rawStr === '') {
      return { raw: '', formatted: '' };
    }

    // Keep track of whether user intended to type a decimal by checking the original input.
    const userTypedDecimal = /[.٫]/.test(rawStr);

    // 1. Sanitize input: convert all digits to English for processing.
    const englishStr = toEnglishDigits(rawStr);

    // Remove thousands separators, normalize all decimal separators to '.'
    const normalizedStr = englishStr.replace(/[٬,]/g, '').replace(/٫/g, '.');

    const { type, decimals } = options;

    // 2. Split into integer and decimal parts
    const parts = normalizedStr.split('.');
    let integerPart = parts[0].replace(/[^0-9]/g, '');
    let decimalPart = parts.length > 1 ? parts[1].replace(/[^0-9]/g, '') : '';

    // 3. Apply type-specific rules
    if (type === 'rial') {
      decimalPart = ''; // Rial is always integer-only.
    } else if (type === 'gold') {
      if (!userTypedDecimal) {
        decimalPart = '';
      } else {
        decimalPart = decimalPart.slice(0, decimals); // Respect decimal precision.
      }
    }

    // 4. Format the integer part with thousands separators
    const formattedInteger = integerPart
      ? new Intl.NumberFormat('en-US').format(BigInt(integerPart))
      : '';

    // 5. Reconstruct the final formatted string
    let formattedValue = formattedInteger;
    if (decimalPart) {
      formattedValue += '.' + decimalPart;
    } else if (type === 'gold' && /[.٫]$/.test(rawStr)) {
      // Preserve trailing dot if user just typed it (e.g., "123.")
      formattedValue += '.';
    }
    
    // 6. Construct the raw value (for v-model)
    const rawValue = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;

    return { raw: rawValue, formatted: formattedValue };
  };

  const handleInput = (e) => {
    if (!e.isTrusted) return; // Ignore self-triggered events

    const options = getOptions();
    const { formatted } = formatValue(el.value, options);
    
    dispatchEvent(formatted);

    // Move cursor to end after the DOM updates. This is the simplest,
    // most robust way to handle caret position in a formatted RTL input.
    setTimeout(setCaretToEnd, 0);
  };

  const handleBlur = () => {
    const options = getOptions();
    const { formatted } = formatValue(el.value, options);
    const persianFormatted = toPersianDigits(formatted);
    if (el.value !== persianFormatted) {
      dispatchEvent(persianFormatted);
    }
  };

  // Add event listeners
  el.addEventListener('input', handleInput);
  el.addEventListener('blur', handleBlur);

  syncFormattedDisplay({ usePersianDigits: document.activeElement !== el });

  // Store cleanup function
  el._numberFormatCleanup = () => {
    el.removeEventListener('input', handleInput);
    el.removeEventListener('blur', handleBlur);
  };
  el._numberFormatSyncDisplay = syncFormattedDisplay;
}

export const numberFormat = {
  mounted(el, binding) {
    if (el.tagName.toUpperCase() === 'INPUT') {
      setupInputFormatting(el, binding);
    }
  },
  updated(el) {
    if (typeof el._numberFormatSyncDisplay === 'function') {
      el._numberFormatSyncDisplay({
        usePersianDigits: document.activeElement !== el,
      });
    }
  },
  beforeUnmount(el) {
    if (el._numberFormatCleanup) {
      el._numberFormatCleanup();
      delete el._numberFormatCleanup;
    }
    if (el._numberFormatSyncDisplay) {
      delete el._numberFormatSyncDisplay;
    }
  },
};
